/**
 * Warum ein Tag nicht freigabefähig ist — in Worten statt in Modellbegriffen.
 *
 * Der Kern liefert Code, Betrag und Gruppe; der Satz entsteht erst hier. So
 * steht die deutsche Zahlenschreibweise an einer Stelle, und der Hinweis kann
 * sagen, was zu tun ist — das weiss der Rechenkern nicht.
 */
import { euro } from "./format";

export interface CalcErrorDto {
  code: string;
  message: string;
  amount?: string | null;
  groupLabel?: string | null;
  /** Nur bei Hinweisen: wer den Betrag beim Tagesausgleich bekommen hat. */
  recipientPersonalNr?: number | null;
  recipientWorkArea?: string | null;
}

export interface ErklaerterFehler {
  /** Ein Satz, der den Zustand benennt. */
  was: string;
  /** Was das für die Arbeit heisst. Leer, wenn es nichts zu raten gibt. */
  wasTun: string | null;
}

/** `SOMMERGARTEN` → `Sommergarten`, `WINTER_WALK` → `Winter Walk`. */
export function topfName(label: string | null | undefined): string {
  if (!label) return "diesem Topf";
  return label
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((t) => t.charAt(0).toUpperCase() + t.slice(1).toLowerCase())
    .join(" ");
}

export function erklaereFehler(e: CalcErrorDto): ErklaerterFehler {
  const topf = topfName(e.groupLabel);
  const betrag = e.amount ? euro(e.amount) : "Trinkgeld";

  // Im Excel-Modus hat der Tagesausgleich das Geld schon weitergereicht. Dann
  // ist nichts zu tun — aber es gehört gesagt, wohin es gegangen ist.
  if (e.recipientPersonalNr != null) {
    return {
      was:
        `${betrag} aus dem Topf ${topf} konnten an niemanden gehen: an diesem Abend hat ` +
        `dort niemand gearbeitet. Der Tagesausgleich hat den Betrag an PersNr ${e.recipientPersonalNr}` +
        `${e.recipientWorkArea ? ` (${e.recipientWorkArea})` : ""} gegeben. So rechnet auch das Excel-Blatt.`,
      wasTun:
        "Nichts, wenn das so gewollt ist. Gehört das Geld woanders hin, fehlen entweder Stunden, oder die Kasse zählte an diesem Abend für einen anderen Topf. Dann in der Kassentabelle ihren Topf umstellen.",
    };
  }

  switch (e.code) {
    case "POOL_WITHOUT_HOURS":
      return {
        was: `${betrag} gehören in den Topf ${topf}, aber niemand hat an diesem Abend in einem Bereich dieses Topfes gearbeitet.`,
        wasTun:
          "Entweder fehlen Stunden im Gastromatic-Export, oder die Kasse zählte an diesem Abend für einen anderen Topf. Im zweiten Fall in der Kassentabelle ihren Topf umstellen.",
      };
    case "POOL_HOURS_ZERO":
      return {
        was: `${betrag} gehören in den Topf ${topf}. Es gibt Stundenzeilen dazu, aber alle stehen auf null.`,
        wasTun: "Stunden am Tagesblatt nachtragen oder die Korrektur zurücknehmen.",
      };
    case "CONTROL_SUM_MISMATCH":
      return {
        was: "Die Summe der Töpfe stimmt nicht mit der Summe der Auszahlungen überein.",
        wasTun: "Das ist ein Fehler in der Berechnung selbst und gehört gemeldet.",
      };
    case "BLOCKING_SESSION_FLAG":
      return {
        was: "Mindestens eine Kasse ist noch nicht geklärt.",
        wasTun: "Im Kassenblock oben öffnen und entscheiden.",
      };
    case "INVALID_PARTITION":
      return {
        was: "Die Aufteilung der Töpfe auf Gruppen geht nicht auf: ein Topf fehlt oder kommt doppelt vor.",
        wasTun: "Oben in der Leiste unter „Berechnung“ neu wählen.",
      };
    default:
      return { was: e.message, wasTun: null };
  }
}

/** Kurzform für Listen: nur der erste Satz, ohne Handlungsempfehlung. */
export function fehlerKurz(e: CalcErrorDto): string {
  return erklaereFehler(e).was;
}
